import type { CSSProperties } from 'react';
import {
  RESPONSE_BUTTON_SURFACES,
  hasUnknownResponseButtonConfigFields,
  type ResponseButtonConfig,
} from '../lib/responseButtons';

interface ResponseButtonConfigSummaryProps {
  config: ResponseButtonConfig;
}

export function ResponseButtonConfigSummary({ config }: ResponseButtonConfigSummaryProps) {
  const hasUnknownFields = hasUnknownResponseButtonConfigFields(config);

  return (
    <div style={styles.wrapper}>
      <div style={styles.summaryRow}>
        <div>
          <div style={styles.summaryLabel}>Top-level</div>
          <div style={styles.summaryValue}>{config.enabled ? 'Enabled' : 'Disabled'}</div>
        </div>
        <div style={styles.summaryMeta}>WhatsApp limit: 3 buttons per message</div>
      </div>

      {hasUnknownFields && (
        <div style={styles.unknownFieldsNote}>
          Additional raw fields are preserved in storage for forward compatibility, even if they are not shown here.
        </div>
      )}

      {RESPONSE_BUTTON_SURFACES.map(({ key, label, description }) => {
        const surface = config[key];
        if (!surface) {
          return null;
        }

        return (
          <div key={key} style={styles.surfaceCard}>
            <div style={styles.surfaceHeader}>
              <div>
                <div style={styles.surfaceTitle}>{label}</div>
                <div style={styles.surfaceDescription}>{description}</div>
              </div>
              <span style={{
                ...styles.statusBadge,
                ...(surface.enabled ? styles.enabledBadge : styles.disabledBadge),
              }}>
                {surface.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>

            <div style={styles.metaGrid}>
              <div>
                <div style={styles.metaLabel}>Mode</div>
                <div style={styles.metaValue}>{surface.mode}</div>
              </div>
              <div>
                <div style={styles.metaLabel}>Max buttons</div>
                <div style={styles.metaValue}>{surface.maxButtons}</div>
              </div>
            </div>

            <div>
              <div style={styles.metaLabel}>Static buttons</div>
              {surface.staticButtons && surface.staticButtons.length > 0 ? (
                <div style={styles.buttonList}>
                  {surface.staticButtons.map((button, index) => (
                    <div key={`${button.id}-${index}`} style={styles.buttonChip}>
                      <span style={styles.buttonLabel}>{button.label}</span>
                      <span style={styles.buttonId}>{button.id}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyText}>No static buttons configured.</div>
              )}
            </div>

            <div>
              <div style={styles.metaLabel}>AI prompt guidance</div>
              {surface.aiPromptOverride ? (
                <pre style={styles.promptBlock}>{surface.aiPromptOverride}</pre>
              ) : (
                <div style={styles.emptyText}>No AI prompt guidance configured.</div>
              )}
            </div>

            <div>
              <div style={styles.metaLabel}>Fallback messages</div>
              {surface.fallbackMessageMapping && Object.keys(surface.fallbackMessageMapping).length > 0 ? (
                <div style={styles.fallbackList}>
                  {Object.entries(surface.fallbackMessageMapping).map(([buttonId, message]) => (
                    <div key={buttonId} style={styles.fallbackCard}>
                      <div style={styles.fallbackId}>{buttonId}</div>
                      <pre style={styles.fallbackMessage}>{message}</pre>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyText}>No fallback messages configured.</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  summaryRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
  summaryLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '4px',
  },
  summaryValue: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#0f172a',
  },
  summaryMeta: {
    fontSize: '12px',
    color: '#64748b',
  },
  surfaceCard: {
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '16px',
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  surfaceHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
  surfaceTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#0f172a',
    marginBottom: '4px',
  },
  surfaceDescription: {
    fontSize: '12px',
    color: '#64748b',
  },
  statusBadge: {
    borderRadius: '999px',
    padding: '6px 10px',
    fontSize: '12px',
    fontWeight: 600,
  },
  enabledBadge: {
    backgroundColor: '#dcfce7',
    color: '#166534',
  },
  disabledBadge: {
    backgroundColor: '#e2e8f0',
    color: '#475569',
  },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '12px',
  },
  metaLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#64748b',
    marginBottom: '4px',
  },
  metaValue: {
    fontSize: '14px',
    color: '#0f172a',
  },
  buttonList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  buttonChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    borderRadius: '999px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    padding: '8px 12px',
  },
  buttonLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#0f172a',
  },
  buttonId: {
    fontSize: '12px',
    color: '#64748b',
    fontFamily: 'monospace',
  },
  fallbackList: {
    display: 'grid',
    gap: '10px',
  },
  fallbackCard: {
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    padding: '12px',
    backgroundColor: '#f8fafc',
  },
  fallbackId: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#475569',
    fontFamily: 'monospace',
    marginBottom: '6px',
  },
  fallbackMessage: {
    margin: 0,
    whiteSpace: 'pre-wrap',
    fontFamily: 'inherit',
    fontSize: '13px',
    lineHeight: 1.5,
    color: '#0f172a',
  },
  promptBlock: {
    margin: 0,
    padding: '12px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    whiteSpace: 'pre-wrap',
    fontFamily: 'inherit',
    fontSize: '13px',
    lineHeight: 1.6,
    color: '#1e293b',
  },
  emptyText: {
    fontSize: '13px',
    color: '#64748b',
    fontStyle: 'italic',
  },
  unknownFieldsNote: {
    padding: '12px 14px',
    borderRadius: '10px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    fontSize: '12px',
    color: '#475569',
    lineHeight: 1.5,
  },
};
